/**
 * What the app remembers about the last few minutes, so a bug report arrives
 * with the evidence already attached.
 *
 * The person reporting is standing at a counter with a customer waiting. They
 * should type one sentence, not answer questions about their browser or try to
 * remember what they clicked. Everything mechanical is captured here.
 *
 * Deliberately in-memory and capped. This is diagnostic context for a report
 * the user chooses to send — not telemetry, not persisted, not sent anywhere
 * unless someone presses the button.
 */

/*
 * From `/react`, the same entry `hub-feedback.tsx` mounts the widget from —
 * **and that is load-bearing, not taste.** The package bundles each dist entry
 * with its own copy of the diagnostics module, so the root entry and the react
 * entry hold two separate buffers. Forwarding into the root while the widget
 * reads the react copy produced reports with an empty trail, silently: every
 * breadcrumb recorded, none of them attached. One entry, one module scope, one
 * buffer. (The split-buffer packaging is a feedback-hub bug to fix upstream;
 * until then this import path is the guarantee.)
 */
import {
  recordBreadcrumb as hubBreadcrumb,
  recordError as hubError,
} from '@refractspaces/feedback-widget/react';

export interface Breadcrumb {
  readonly at: string;
  readonly kind: string;
  readonly detail: string;
}

const MAX_BREADCRUMBS = 30;
const MAX_ERRORS = 15;
/** Long enough to be useful in a report, short enough not to be a payload. */
const MAX_DETAIL = 300;
const MAX_ERROR = 1000;

const breadcrumbs: Breadcrumb[] = [];
const recentErrors: string[] = [];

/**
 * The clock on the wall the reporter was looking at.
 *
 * This used to slice `toISOString()`, which is UTC — so the first real report
 * from the hosted store arrived with a header saying it happened at 00:01
 * (America/Chicago) above a breadcrumb trail starting at 04:59. Two timestamps
 * five hours apart describing the same thirty seconds, in one email.
 *
 * Local, because these are the operator's own timeline and "it happened just
 * after five" is what they will say on the phone. The report header names the
 * timezone, so the two now agree.
 */
function clockTime(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

export function recordBreadcrumb(kind: string, detail: string): void {
  breadcrumbs.push({ at: clockTime(), kind, detail: detail.slice(0, MAX_DETAIL) });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();

  /*
   * Forwarded to the hub widget's own trail, so the sixty-odd call sites that
   * already narrate what the operator did feed whichever feedback path is
   * live. The widget cannot know what *this* app considers a meaningful
   * action (INTEGRATION §3.2) — these calls are exactly that knowledge, and
   * re-instrumenting every screen against a second API would be the
   * two-copies-that-drift shape this codebase keeps paying for.
   *
   * Deliberate actions only: window errors and network flaps are captured by
   * the widget's own global handlers when it mounts, so forwarding those from
   * `installDiagnostics` would record each one twice.
   */
  hubBreadcrumb(kind, detail);
}

export function recordError(message: string): void {
  /*
   * Truncate the finished line, not the message inside it.
   *
   * This used to cut the message to 1000 and *then* prefix the clock, making
   * the entry 1010 characters — ten past what the API accepted. Every report
   * carrying a long error was refused, and because this buffer lives until the
   * tab is reloaded, so was every report after it: the payload is rebuilt from
   * the same memory each time (2026-08-11). The API now trims rather than
   * refuses, so this can no longer break a report on its own. It is fixed
   * anyway, because a cap should mean the thing it says it means.
   */
  const entry = `${clockTime()}  ${message}`.slice(0, MAX_ERROR);
  // Repeated identical errors (a retrying query) add noise, not information.
  if (recentErrors[recentErrors.length - 1]?.slice(9) === entry.slice(9)) return;
  recentErrors.push(entry);
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();

  // Forwarded for the same reason breadcrumbs are — and these are the errors
  // the widget's own handlers can never see: React Query catches a failure
  // before it becomes an unhandled rejection, so app.tsx reports it here.
  hubError(message);
}

export interface ClientContext {
  readonly userAgent: string;
  readonly language: string;
  readonly timezone: string;
  readonly viewport: { width: number; height: number };
  readonly screen: { width: number; height: number };
  readonly online: boolean;
  readonly url: string;
  readonly appVersion: string;
}

export function collectClientContext(): ClientContext {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: { width: window.screen.width, height: window.screen.height },
    online: navigator.onLine,
    url: window.location.href,
    appVersion: import.meta.env.VITE_APP_VERSION ?? 'dev',
  };
}

/**
 * The trail as it will be sent, separate from the browser probes above.
 *
 * Split out so the size invariant this file owes the API can be tested without
 * standing up a DOM — the caps are the part that broke once, and an untestable
 * cap is how it stayed broken.
 */
export function collectTrail(): { breadcrumbs: Breadcrumb[]; recentErrors: string[] } {
  return { breadcrumbs: [...breadcrumbs], recentErrors: [...recentErrors] };
}

export function collectDiagnostics(): {
  client: ClientContext;
  breadcrumbs: Breadcrumb[];
  recentErrors: string[];
} {
  return { client: collectClientContext(), ...collectTrail() };
}

let installed = false;

/**
 * Catch what the user never sees: thrown errors and rejected promises.
 *
 * **Not called when the feedback hub is live** (`app.tsx`): the widget installs
 * its own identical handlers when it mounts, and since `recordBreadcrumb` and
 * `recordError` forward into the widget's trail, running both sets would record
 * every window error and every network flap twice. One owner per signal — the
 * widget when it is mounted, this when it is not.
 */
export function installDiagnostics(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event) => {
    recordError(`${event.message} (${event.filename}:${event.lineno})`);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    recordError(
      `Unhandled rejection: ${reason instanceof Error ? reason.message : String(reason)}`,
    );
  });

  window.addEventListener('offline', () => recordBreadcrumb('network', 'went offline'));
  window.addEventListener('online', () => recordBreadcrumb('network', 'back online'));
}
